package com.exura.pages.publicplans

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.features.publicplans.domain.model.ProviderPublicPlan
import com.exura.features.publicplans.domain.usecase.CreateNewPublicPlanUseCase
import com.exura.features.publicplans.domain.usecase.ListPublicPlansUseCase
import com.exura.navigation.Route
import com.exura.navigation.navigateTo
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.core.PageContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

data class PublicPlanListUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val plans: List<ProviderPublicPlan> = emptyList()
)

class PublicPlanListController(
    private val scope: CoroutineScope,
    private val context: PageContext,
    private val listPublicPlansUseCase: ListPublicPlansUseCase,
    private val createNewPublicPlanUseCase: CreateNewPublicPlanUseCase,
) {
    var uiState by mutableStateOf(PublicPlanListUiState())
        private set

    private var loadJob: Job? = null

    init {
        loadPlans()
    }

    fun loadPlans() {
        loadJob?.cancel()

        loadJob = scope.launch {
            uiState = uiState.copy(isLoading = true, error = null)

            listPublicPlansUseCase()
                .onSuccess { flow ->
                    flow.collect { plans ->
                        uiState = uiState.copy(
                            isLoading = false,
                            plans = plans
                        )
                    }
                }
                .onFailure { error ->
                    uiState = uiState.copy(
                        isLoading = false,
                        error = error.message
                    )
                }
        }
    }

    fun onPlanClicked(planId: String) {
        context.navigateTo(Route.WithParams.toPublicPlanDetails(planId))
    }

    fun onPlanSelectedForPublish(planId: String) {
        scope.launch {
            uiState = uiState.copy(isLoading = true)

            createNewPublicPlanUseCase(planId)
                .onSuccess { newPublicPlan ->
                    uiState = uiState.copy(isLoading = false)
                    newPublicPlan.id?.let { publicEntryId ->
                        onPlanClicked(publicEntryId)
                    }
                }
                .onFailure { error ->
                    uiState = uiState.copy(
                        isLoading = false,
                        error = error.message
                    )
                }
        }
    }
}

@Composable
fun rememberPublicPlanListController(
    scope: CoroutineScope,
    context: PageContext,
): PublicPlanListController {
    val listPublicPlansUseCase = injectAndRemember<ListPublicPlansUseCase>()
    val createPublicPlanUseCase = injectAndRemember<CreateNewPublicPlanUseCase>()

    return remember(scope, context) {
        PublicPlanListController(
            scope = scope,
            context = context,
            listPublicPlansUseCase = listPublicPlansUseCase,
            createNewPublicPlanUseCase = createPublicPlanUseCase,
        )
    }
}
